import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../../interceptors";
import {
  fetchJobsForScope,
  selectScopedJobIds,
  selectScopeJobs,
  selectScopeJobsLoaded,
} from "../../../redux/slices/job-scope-slice";
import { SEED_TEMPLATES } from "../email-templates/seed-templates";

/* eslint-disable react-hooks/exhaustive-deps */

export default function TalentPools() {
  const [params, setParams] = useSearchParams();
  const scope = useSelector(selectScopedJobIds);

  const poolIdParam = params.get("poolId");
  const action = params.get("action");
  const candidateId = params.get("candidateId");
  const mode = params.get("mode") || "individual";

  const [pools, setPools] = useState([]);
  const [selectedPoolId, setSelectedPoolId] = useState(poolIdParam || null);
  const [inviteOpen, setInviteOpen] = useState(action === "invite");
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    document.title = "Talent pools - Ample Hire";
  }, []);

  // Pools are tenant-scoped by the JWT (MultitenantDataSource) — no orgId or
  // jobIds query needed; the backend returns a raw array for the caller's tenant.
  useEffect(() => {
    axiosInstance
      .get("/job-posting/talent-pools")
      // Tolerates both shapes: the endpoint now returns { success, data } like the
      // rest of the pool API, but older builds returned a bare array.
      .then((r) =>
        setPools(
          Array.isArray(r.data)
            ? r.data
            : Array.isArray(r.data?.data)
              ? r.data.data
              : r.data?.pools || [],
        ),
      )
      .catch(() => setPools([]));
  }, []);

  useEffect(() => {
    if (poolIdParam) setSelectedPoolId(poolIdParam);
    if (action === "invite") setInviteOpen(true);
  }, [poolIdParam, action]);

  const closeInvite = () => {
    setInviteOpen(false);
    const next = new URLSearchParams(params);
    next.delete("action");
    next.delete("candidateId");
    next.delete("mode");
    setParams(next, { replace: true });
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Talent pools</h1>
          <p className="text-sm text-vf-muted mt-1">
            Group candidates by source, fit, or campaign — invite from a pool
            with one click.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setUploadOpen(true)}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Upload resumes
          </button>
          <button
            onClick={() => setInviteOpen(true)}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium"
          >
            + Invite candidates
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-vf-muted uppercase mb-3">
            Pools ({pools.length})
          </div>
          {pools.length === 0 ? (
            <div className="text-xs text-vf-muted py-6 text-center">
              No pools yet. Click <strong>Upload resumes</strong> to create one.
            </div>
          ) : (
            <ul className="space-y-1">
              {pools.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => {
                      setSelectedPoolId(p.id);
                      const next = new URLSearchParams(params);
                      next.set("poolId", p.id);
                      setParams(next, { replace: true });
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${selectedPoolId == p.id ? "bg-vf-tint text-vf-primary font-medium" : "hover:bg-vf-tint text-vf-base"}`}
                  >
                    <span className="truncate">{p.name}</span>
                    <span className="text-[10px] text-vf-muted">
                      {p.candidateCount ?? 0}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl p-4 min-h-[300px]">
          {selectedPoolId ? (
            <PoolMembers
              poolId={selectedPoolId}
              pool={pools.find(
                (p) => String(p.id) === String(selectedPoolId),
              )}
              jobIds={scope}
            />
          ) : (
            <div className="text-center text-sm text-vf-muted py-12">
              Pick a pool on the left, or click{" "}
              <strong>+ Invite candidates</strong> to start a new invite flow.
            </div>
          )}
        </div>
      </div>

      {inviteOpen && (
        <InviteModal
          mode={mode}
          poolId={selectedPoolId}
          candidateId={candidateId}
          jobIds={scope}
          interviewId={params.get("interviewId")}
          onClose={closeInvite}
        />
      )}

      {uploadOpen && (
        <CreatePoolModal
          onClose={() => setUploadOpen(false)}
          onCreated={(pool) => {
            setPools((prev) => [
              {
                id: pool.id,
                name: pool.name,
                description: pool.description,
                candidateCount: pool.candidateCount ?? pool.added ?? 0,
                orgId: pool.orgId,
                createdAt: pool.createdAt,
              },
              ...prev.filter((p) => p.id !== pool.id),
            ]);
            setSelectedPoolId(pool.id);
            const next = new URLSearchParams(params);
            next.set("poolId", pool.id);
            setParams(next, { replace: true });
          }}
        />
      )}
    </div>
  );
}

// Step 3: members of the selected pool. Response is wrapped { success, data:[] }.
function PoolMembers({ poolId, pool, jobIds = [] }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axiosInstance.get(
        `/job-posting/talent-pools/${poolId}/candidates`,
      );
      setMembers(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to load members.",
      );
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!poolId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axiosInstance.get(
          `/job-posting/talent-pools/${poolId}/candidates`,
        );
        if (cancelled) return;
        setMembers(Array.isArray(r.data?.data) ? r.data.data : []);
      } catch (e) {
        if (cancelled) return;
        setError(
          e.response?.data?.message || e.message || "Failed to load members.",
        );
        setMembers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolId]);

  const inviteBadge = (status) => {
    if (status === "INVITED")
      return (
        <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
          Invited
        </span>
      );
    return (
      <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-500">
        Not invited
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-vf-base">
            {pool?.name || `Pool #${poolId}`}
          </h2>
          {pool?.description && (
            <p className="text-xs text-vf-muted mt-0.5">{pool.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-vf-muted whitespace-nowrap">
            {members.length} member{members.length === 1 ? "" : "s"}
          </span>
          <button
            onClick={() => setInviteOpen(true)}
            disabled={members.length === 0}
            title={
              members.length === 0 ? "No members to invite" : "Invite this pool"
            }
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
          >
            Send invite
          </button>
        </div>
      </div>

      {inviteOpen && (
        <PoolInviteModal
          poolId={poolId}
          poolName={pool?.name}
          jobIds={jobIds}
          onClose={() => setInviteOpen(false)}
          onSent={() => {
            setInviteOpen(false);
            load();
          }}
        />
      )}

      {loading ? (
        <div className="text-xs text-vf-muted py-10 text-center">
          Loading members…
        </div>
      ) : error ? (
        <div className="text-xs text-red-600 py-10 text-center">{error}</div>
      ) : members.length === 0 ? (
        <div className="text-xs text-vf-muted py-10 text-center">
          No candidates in this pool yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-vf-muted">
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Email</th>
                <th className="px-3 py-2 font-semibold">Phone</th>
                <th className="px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-vf-base truncate">
                    {m.name || "—"}
                  </td>
                  <td className="px-3 py-2 text-vf-muted truncate">
                    {m.email || "—"}
                  </td>
                  <td className="px-3 py-2 text-vf-muted whitespace-nowrap">
                    {m.phone || "—"}
                  </td>
                  <td className="px-3 py-2">{inviteBadge(m.inviteStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Step 4: invite a whole pool to one or more jobs. Mirrors the legacy invite
// flow's template sourcing (getCandidatesTemplates → s3 keys), but the backend
// fills candidateInfo from the pool, so we never send candidates here.
// jobId mode: ?jobId=123,456 + interviewRoundName (matched by name on each job).
// Step 4: invite a whole pool. Job is picked from a dropdown, then the round
// dropdown is populated from that job's interviewRounds.
//
// Sends via /interview-acceptance/send — the same path the "+ Invite candidates"
// modal uses, whose email template ships with the app (classpath
// interview-acceptance/email-templates/) and needs no per-org upload. The older
// /talent-pools/{id}/invite path required a .docx in S3 under
// email_templates/{org}/{hiringType}/{round}/{inviteType}/, which no screen can
// create — that is why this dialog used to dead-end on "No templates for this
// round". The backend resolves interviewRoundName to an interviewId.
function PoolInviteModal({ poolId, poolName, jobIds = [], onClose, onSent }) {
  const dispatch = useDispatch();
  const allJobs = useSelector(selectScopeJobs);
  const jobsLoaded = useSelector(selectScopeJobsLoaded);

  const [jobId, setJobId] = useState(jobIds[0] != null ? String(jobIds[0]) : "");
  const [rounds, setRounds] = useState([]);
  const [roundName, setRoundName] = useState("");
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [roundsError, setRoundsError] = useState(null);

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Populate the job dropdown. The scope slice already caches the org's
  // published jobs; fetch only if nothing has loaded them yet this session.
  useEffect(() => {
    if (!jobsLoaded) dispatch(fetchJobsForScope());
  }, [jobsLoaded, dispatch]);

  // Pool members — these are the recipients.
  useEffect(() => {
    let cancelled = false;
    setLoadingMembers(true);
    axiosInstance
      .get(`/job-posting/talent-pools/${poolId}/candidates`)
      .then((r) => {
        if (cancelled) return;
        setMembers(Array.isArray(r.data?.data) ? r.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [poolId]);

  // Rounds for the selected job.
  useEffect(() => {
    setRoundName("");
    setRounds([]);
    setRoundsError(null);
    if (!jobId) return;
    let cancelled = false;
    setLoadingRounds(true);
    axiosInstance
      .post("/job-posting/job-details/get", { jobId: Number(jobId) })
      .then((r) => {
        if (cancelled) return;
        const d = r.data?.data || {};
        // interviewRounds arrives either as a real array or a comma string, and
        // legacy rows can hold a JSON-encoded array inside the element.
        const raw = Array.isArray(d.interviewRounds)
          ? d.interviewRounds
          : String(d.interviewRounds || "").split(",");
        const cleaned = raw
          .map((s) => String(s == null ? "" : s).replace(/[[\]"]/g, "").trim())
          .filter(Boolean);
        setRounds([...new Set(cleaned)]);
      })
      .catch((e) => {
        if (!cancelled)
          setRoundsError(e?.message || "Couldn't load rounds for this job.");
      })
      .finally(() => {
        if (!cancelled) setLoadingRounds(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const reachable = members.filter((m) => m.email);
  const unreachable = members.length - reachable.length;
  const canSend =
    !!jobId && !!roundName && reachable.length > 0 && !sending && !loadingMembers;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await axiosInstance.post(
        "/job-posting/interview-acceptance/send",
        {
          jobId: Number(jobId),
          interviewRoundName: roundName,
          templateId: "interview-acceptance",
          candidates: reachable.map((m) => ({
            email: m.email,
            ...(m.name ? { candidateName: m.name } : {}),
            ...(m.phone ? { phone: m.phone } : {}),
          })),
        },
      );
      const sent = r.data?.sent ?? 0;
      const failed = r.data?.failed ?? 0;
      const skipped = r.data?.skipped ?? 0;
      if (sent === 0) {
        throw new Error(
          failed > 0
            ? `No invites sent — ${failed} failed.`
            : "No invites were sent.",
        );
      }
      setSuccess(
        `Sent ${sent} invite${sent === 1 ? "" : "s"}` +
          (failed ? `, ${failed} failed` : "") +
          (skipped ? `, ${skipped} skipped` : "") +
          ".",
      );
      setTimeout(() => onSent?.(), 1200);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to send invites.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-vf-base">
              Invite pool{poolName ? ` · ${poolName}` : ""}
            </h2>
            <p className="text-xs text-vf-muted mt-1">
              {loadingMembers
                ? "Loading pool members…"
                : `${reachable.length} of ${members.length} member${members.length === 1 ? "" : "s"} can be emailed.`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-vf-muted hover:text-vf-base text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Job <span className="text-red-500">*</span>
            </span>
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary"
            >
              <option value="">
                {!jobsLoaded ? "Loading jobs…" : "Select a job"}
              </option>
              {allJobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                  {j.location ? ` — ${j.location}` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Interview round <span className="text-red-500">*</span>
            </span>
            <select
              value={roundName}
              onChange={(e) => setRoundName(e.target.value)}
              disabled={!jobId || loadingRounds || rounds.length === 0}
              className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary disabled:opacity-50"
            >
              <option value="">
                {!jobId
                  ? "Pick a job first"
                  : loadingRounds
                    ? "Loading rounds…"
                    : rounds.length === 0
                      ? "This job has no interview rounds"
                      : "Select a round"}
              </option>
              {rounds.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {roundsError && (
              <span className="text-[11px] text-red-600 mt-1 inline-block">
                {roundsError}
              </span>
            )}
            {jobId && !loadingRounds && !roundsError && rounds.length === 0 && (
              <span className="text-[11px] text-amber-700 mt-1 inline-block">
                Add a round to this job under Define Interview Rounds, then
                reopen this dialog.
              </span>
            )}
          </label>

          <div className="text-[11px] text-vf-muted border border-dashed border-slate-200 rounded p-3">
            Each member gets the Interview Acceptance email with Accept / Reject
            links. The interview link is sent automatically once they accept.
            {unreachable > 0 && (
              <span className="block text-amber-700 mt-1">
                {unreachable} member{unreachable === 1 ? "" : "s"} skipped — no
                email address on file.
              </span>
            )}
          </div>
        </div>

        {error && (
          <div
            className="mt-3 px-3 py-2 text-xs rounded-lg"
            style={{ background: "rgba(220,38,38,0.08)", color: "#DC2626", border: "1px solid rgba(220,38,38,0.2)" }}
          >
            {error}
          </div>
        )}
        {success && (
          <div
            className="mt-3 px-3 py-2 text-xs rounded-lg"
            style={{ background: "rgba(22,163,74,0.08)", color: "#16A34A", border: "1px solid rgba(22,163,74,0.2)" }}
          >
            {success}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={!canSend}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send invites"}
          </button>
        </div>
      </div>
    </div>
  );
}

const POOL_FILE_RE = /\.(pdf|docx?|txt)$/i;

// Step 1 of the talent-pool flow: create a pool with optional resume files.
// POST /job-posting/talent-pools (multipart). orgId falls back to the token,
// so we only send it when localStorage has one. Contact-info arrays
// (names/emails/phones) are omitted — the backend parses resumes itself; they
// only matter when the client has pre-parsed data to index-align.
function CreatePoolModal({ onClose, onCreated }) {
  const inputRef = useRef(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter(
      (f) => f.type === "application/pdf" || POOL_FILE_RE.test(f.name),
    );
    if (arr.length === 0) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const next = [...prev];
      for (const f of arr) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          next.push(f);
        }
      }
      return next;
    });
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const canCreate = name.trim().length > 0 && !submitting;

  const create = async () => {
    if (!canCreate) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      if (description.trim()) fd.append("description", description.trim());
      const orgId = localStorage.getItem("organizationId");
      if (orgId) fd.append("orgId", orgId);
      files.forEach((f) => fd.append("files", f));
      // Don't set Content-Type — axios derives the multipart boundary.
      const r = await axiosInstance.post("/job-posting/talent-pools", fd);
      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to create pool.");
      }
      onCreated?.(r.data?.data || {});
      onClose();
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to create pool.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fmtSize = (b) =>
    b < 1024
      ? `${b} B`
      : b < 1048576
        ? `${(b / 1024).toFixed(0)} KB`
        : `${(b / 1048576).toFixed(1)} MB`;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-vf-base">
              Create talent pool
            </h2>
            <p className="text-xs text-vf-muted mt-1">
              Name the pool and optionally drop in resumes — we'll parse and add
              them as members.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-vf-muted hover:text-vf-base text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Pool name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Backend Q3 2026"
              autoFocus
              className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary focus:ring-2 focus:ring-vf-highlight"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Description
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="e.g. Senior Java / Node candidates"
              className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary focus:ring-2 focus:ring-vf-highlight"
            />
          </label>

          <div>
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Resumes
            </span>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              className={`mt-1 cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
                dragOver
                  ? "border-vf-accent bg-vf-tint"
                  : "border-slate-200 hover:border-vf-highlight"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
              <p className="text-sm text-vf-base font-medium">
                Drop resumes here or click to choose
              </p>
              <p className="text-[11px] text-vf-muted mt-0.5">
                PDF, DOCX, TXT · optional
              </p>
            </div>

            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}:${f.size}`}
                    className="flex items-center justify-between px-3 py-1.5 rounded bg-slate-50 text-xs"
                  >
                    <span className="truncate text-vf-base">{f.name}</span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-vf-muted">{fmtSize(f.size)}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="text-vf-muted hover:text-red-600"
                        aria-label={`Remove ${f.name}`}
                      >
                        ×
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-3 px-3 py-2 bg-red-50 text-red-700 text-xs rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Cancel
          </button>
          <button
            onClick={create}
            disabled={!canCreate}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
          >
            {submitting
              ? "Creating…"
              : `Create pool${files.length ? ` (${files.length})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function InviteModal({
  mode,
  poolId,
  candidateId,
  jobIds,
  interviewId,
  onClose,
}) {
  const scopedJobs = useSelector(selectScopeJobs);
  const firstScopedJob = scopedJobs.find((j) => jobIds.includes(j.id));
  const role = firstScopedJob?.name || "";
  const company = localStorage.getItem("organizationName") || "";
  const jobId = firstScopedJob?.id ?? jobIds[0] ?? null;

  const [activeMode, setActiveMode] = useState(mode || "individual");
  const [candidateName, setCandidateName] = useState("");
  const [phone, setPhone] = useState("");
  const [emails, setEmails] = useState("");
  const [opens, setOpens] = useState(toLocalInput(new Date()));
  const [expires, setExpires] = useState(
    toLocalInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
  );
  const [timezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // The pool's own members. /interview-acceptance/send takes explicit recipients
  // and knows nothing about pools, so "from pool" and the ?candidateId= deep link
  // both have to be resolved to real email addresses here.
  const [poolMembers, setPoolMembers] = useState([]);
  const [membersError, setMembersError] = useState(null);

  useEffect(() => {
    if (!poolId) {
      setPoolMembers([]);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get(`/job-posting/talent-pools/${poolId}/candidates`)
      .then((r) => {
        if (cancelled) return;
        setPoolMembers(Array.isArray(r.data?.data) ? r.data.data : []);
        setMembersError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setPoolMembers([]);
        setMembersError(e?.message || "Could not load pool members.");
      });
    return () => {
      cancelled = true;
    };
  }, [poolId]);

  const toRecipient = (m) => ({
    email: m.email,
    ...(m.name ? { candidateName: m.name } : {}),
    ...(m.phone ? { phone: m.phone } : {}),
  });

  const candidates = useMemo(() => {
    // Deep-linked single candidate: look the member up so the request carries an
    // email. parseCandidates() alone emits { candidateId } with no email, and
    // CandidateRecipient has no candidateId field — the send always failed.
    if (candidateId) {
      const m = poolMembers.find((x) => String(x.id) === String(candidateId));
      if (m?.email) return [toRecipient(m)];
      return [];
    }
    if (activeMode === "from-pool") {
      return poolMembers.filter((m) => m.email).map(toRecipient);
    }
    return parseCandidates(
      emails,
      null,
      activeMode === "individual" ? candidateName : "",
      activeMode === "individual" ? phone : "",
    );
  }, [candidateId, poolMembers, activeMode, emails, candidateName, phone]);

  const unreachable =
    activeMode === "from-pool"
      ? poolMembers.filter((m) => !m.email).length
      : 0;

  const canSend = candidates.length > 0 && jobIds.length > 0 && !sending;

  const acceptanceTemplate = useMemo(() => {
    let all = SEED_TEMPLATES;
    try {
      const stored = localStorage.getItem("ample_templates");
      if (stored) all = JSON.parse(stored);
    } catch {
      /* fall back to seed */
    }
    const emailAcceptance = all.filter(
      (t) => t.channel === "EMAIL" && t.templateType === "ACCEPTANCE",
    );
    return (
      emailAcceptance.find((t) => t.isDefault) || emailAcceptance[0] || null
    );
  }, []);

  const previewCtx = {
    candidateName,
    email: emails,
    opens,
    expires,
    role,
    company,
  };

  const previewSubject = useMemo(
    () => fillPlaceholders(acceptanceTemplate?.subject || "", previewCtx),
    [acceptanceTemplate, candidateName, emails, opens, expires, role, company],
  );

  const previewBody = useMemo(
    () => fillPlaceholders(acceptanceTemplate?.body || "", previewCtx),
    [acceptanceTemplate, candidateName, emails, opens, expires, role, company],
  );

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        jobId: jobId != null ? Number(jobId) : null,
        interviewId: interviewId != null ? Number(interviewId) : null,
        templateId: "interview-acceptance",
        subjectOverride: null,
        bodyOverride: null,
        templateVarsOverride: null,
        candidates,
      };
      const r = await axiosInstance.post(
        "/job-posting/interview-acceptance/send",
        payload,
      );
      // SendAcceptanceResponse reports sent/skipped/failed separately — reporting
      // only `sent` (or falling back to the requested count) showed "Sent 0
      // invites." in green when every recipient failed.
      const sent = r.data?.sent ?? 0;
      const failed = r.data?.failed ?? 0;
      const skipped = r.data?.skipped ?? 0;
      if (sent === 0) {
        throw new Error(
          failed > 0
            ? `No invites sent — ${failed} failed.`
            : "No invites were sent.",
        );
      }
      setSuccess(
        `Sent ${sent} invite${sent === 1 ? "" : "s"}` +
          (failed ? `, ${failed} failed` : "") +
          (skipped ? `, ${skipped} skipped` : "") +
          ".",
      );
      setTimeout(onClose, 1200);
    } catch (e) {
      setError(e?.message || "Failed to send invites.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-fade-in"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="card-glass-elevated animate-scale-in w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto"
        style={{ borderRadius: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(123,90,196,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7B5AC4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#1A2332", margin: 0, lineHeight: 1.2 }}>
                Invite candidates
              </h2>
              <p className="text-xs text-vf-muted" style={{ marginTop: 2 }}>
                {jobIds.length} job{jobIds.length === 1 ? "" : "s"} scoped
                {poolId && ` · Pool #${poolId}`}
                {candidateId && ` · Candidate #${candidateId}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "var(--ah-surface)", color: "#64748B", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, lineHeight: 1, flexShrink: 0 }}
          >
            ×
          </button>
        </div>

        <div className="flex gap-1 mb-5" style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          {["individual", "bulk", "from-pool"].map((m) => (
            <button
              key={m}
              onClick={() => setActiveMode(m)}
              className={`px-3.5 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${activeMode === m ? "border-vf-primary text-vf-primary" : "border-transparent text-vf-muted hover:text-vf-base"}`}
            >
              {m === "individual"
                ? "Individual"
                : m === "bulk"
                  ? "Bulk"
                  : "From pool"}
            </button>
          ))}
        </div>

        {activeMode === "from-pool" ? (
          <div className="text-sm text-vf-muted py-6 text-center border border-dashed border-slate-200 rounded">
            {!poolId ? (
              "Pick a pool from the list on the left first."
            ) : membersError ? (
              <span className="text-red-600">{membersError}</span>
            ) : (
              <>
                Will invite {candidates.length} candidate
                {candidates.length === 1 ? "" : "s"} from pool #{poolId}.
                {unreachable > 0 && (
                  <span className="block text-[11px] text-amber-700 mt-1">
                    {unreachable} member{unreachable === 1 ? "" : "s"} skipped —
                    no email address on file.
                  </span>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {activeMode === "individual" && (
              <>
                <label className="block">
                  <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
                    Name
                  </span>
                  <input
                    type="text"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="e.g. Alex Morgan"
                    className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary focus:ring-2 focus:ring-vf-highlight"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
                    Mobile number
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                    inputMode="tel"
                    autoComplete="tel"
                    className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary focus:ring-2 focus:ring-vf-highlight"
                  />
                </label>
              </>
            )}
            <label className="block">
              <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
                {activeMode === "bulk"
                  ? "Candidates (one per line — Name <email> or just email)"
                  : "Email address"}
              </span>
              <textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={activeMode === "bulk" ? 6 : 2}
                placeholder={
                  activeMode === "bulk"
                    ? "Alex Morgan <alex@example.com>\nBailey Patel, bailey@example.com\ncasey@example.com"
                    : "alex@example.com"
                }
                className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary focus:ring-2 focus:ring-vf-highlight"
              />
              <span className="text-[11px] text-vf-muted mt-1 inline-block">
                {candidates.length} valid{" "}
                {candidates.length === 1 ? "candidate" : "candidates"} parsed
              </span>
            </label>
          </div>
        )}

        {acceptanceTemplate && activeMode !== "from-pool" && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-vf-muted uppercase tracking-wider">
                Acceptance email preview
              </span>
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{ background: "rgba(123,90,196,0.1)", color: "#7B5AC4" }}
              >
                Template · {acceptanceTemplate.name}
              </span>
            </div>
            <div className="rounded-lg overflow-hidden border border-subtle">
              <div className="px-4 py-2 flex items-center gap-2 text-[11px] text-vf-muted" style={{ background: "var(--ah-surface)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <span className="w-2 h-2 rounded-full bg-yellow-400" />
                <span className="w-2 h-2 rounded-full bg-green-400" />
                <span className="ml-2">Email preview</span>
              </div>
              <div className="bg-white p-4 text-xs">
                <div className="text-vf-muted mb-1">
                  <span>To: </span>
                  <span className="text-vf-base">
                    {emails || "candidate@example.com"}
                  </span>
                </div>
                <div className="text-vf-muted mb-3">
                  <span>Subject: </span>
                  <strong className="text-vf-base">
                    {previewSubject || "(no subject)"}
                  </strong>
                </div>
                <div
                  className="border-t pt-3 text-sm leading-relaxed max-h-72 overflow-y-auto"
                  style={{ borderColor: "rgba(99,102,241,0.1)" }}
                  dangerouslySetInnerHTML={{ __html: previewBody }}
                />
              </div>
            </div>
            <p className="text-[11px] text-vf-muted mt-1.5">
              Edit this template at <code>/admin/email-templates</code>.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-4">
          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Opens
            </span>
            <input
              type="datetime-local"
              value={opens}
              onChange={(e) => setOpens(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-vf-muted uppercase tracking-wider">
              Expires
            </span>
            <input
              type="datetime-local"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 text-sm rounded-lg bg-vf-tint border border-subtle focus:outline-none focus:border-vf-primary"
            />
          </label>
        </div>
        <p className="text-[11px] text-vf-muted mt-2">Timezone: {timezone}</p>

        {error && (
          <div className="mt-3 px-3 py-2 text-xs rounded-lg" style={{ background: "rgba(220,38,38,0.08)", color: "#DC2626", border: "1px solid rgba(220,38,38,0.2)" }}>
            {error}
          </div>
        )}
        {success && (
          <div className="mt-3 px-3 py-2 text-xs rounded-lg" style={{ background: "rgba(22,163,74,0.08)", color: "#16A34A", border: "1px solid rgba(22,163,74,0.2)" }}>
            {success}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="text-xs font-medium text-vf-muted hover:bg-vf-tint hover:text-vf-base"
            style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid var(--ah-border)", background: "transparent", cursor: "pointer", transition: "all 0.15s" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="text-xs font-semibold text-white disabled:opacity-50"
            style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #7B5AC4, #6A4DB3)", cursor: canSend ? "pointer" : "not-allowed", boxShadow: "0 4px 12px rgba(123,90,196,0.25)" }}
          >
            {sending ? "Sending…" : `Send ${candidates.length || ""}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/;

// Parse a single line in one of these formats:
//   "Alex Morgan <alex@example.com>"
//   "Alex Morgan, alex@example.com"
//   "alex@example.com"
function parseCandidateLine(rawLine) {
  const line = (rawLine || "").trim();
  if (!line) return null;

  const angle = line.match(/^(.*?)<\s*([^>]+)\s*>\s*$/);
  if (angle) {
    const name = angle[1].trim().replace(/[,;]\s*$/, "");
    const email = angle[2].trim();
    if (EMAIL_RE.test(email)) return { name, email };
  }

  const commaIdx = line.lastIndexOf(",");
  if (commaIdx > 0) {
    const name = line.slice(0, commaIdx).trim();
    const tail = line.slice(commaIdx + 1).trim();
    const m = tail.match(EMAIL_RE);
    if (m && m[0] === tail) return { name, email: m[0] };
  }

  const emailMatch = line.match(EMAIL_RE);
  if (emailMatch && emailMatch[0] === line) {
    return { name: "", email: emailMatch[0] };
  }

  return null;
}

function parseCandidates(input, candidateId, name = "", phone = "") {
  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();
  if (candidateId) {
    const entry = { candidateId: Number(candidateId) };
    if (trimmedName) entry.candidateName = trimmedName;
    if (trimmedPhone) entry.phone = trimmedPhone;
    return [entry];
  }

  const lines = (input || "").split(/\r?\n/);
  const parsed = [];
  const seen = new Set();
  for (const line of lines) {
    // Allow comma-separated emails on a single line ONLY when there's no name pattern.
    // Heuristic: if no "<...>" and no clear "Name, email" pattern, treat commas as splitters.
    const sub =
      /<.*>/.test(line) || /,\s*[\w.+-]+@[\w-]+\.[\w.-]+\s*$/.test(line)
        ? [line]
        : line.split(",");
    for (const piece of sub) {
      const c = parseCandidateLine(piece);
      if (c && !seen.has(c.email.toLowerCase())) {
        seen.add(c.email.toLowerCase());
        parsed.push(c);
      }
    }
  }

  return parsed.map((c, _, arr) => {
    const entry = { email: c.email };
    const isSingle = arr.length === 1;
    const useFallbackName = isSingle && !c.name && trimmedName;
    const finalName = c.name || (useFallbackName ? trimmedName : "");
    if (finalName) entry.candidateName = finalName;
    if (isSingle && trimmedPhone) entry.phone = trimmedPhone;
    return entry;
  });
}

function formatDateTime(dt) {
  if (!dt || isNaN(dt)) return "";
  return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function fillPlaceholders(template, ctx = {}) {
  const opensDate = ctx.opens ? new Date(ctx.opens) : null;
  const expiresDate = ctx.expires ? new Date(ctx.expires) : null;
  const sample = {
    candidateName: ctx.candidateName?.trim() || "Candidate Name",
    candidateEmail: (ctx.email || "").trim() || "candidate@example.com",
    jobTitle: ctx.role?.trim() || "the role",
    companyName: ctx.company?.trim() || "Your company",
    interviewDate: formatDateTime(opensDate) || "the upcoming window",
    interviewTime:
      opensDate && !isNaN(opensDate)
        ? opensDate.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "",
    interviewExpiry: formatDateTime(expiresDate) || "the deadline",
    acceptUrl: "https://app.example.com/invite/<token>",
    rejectUrl: "https://app.example.com/invite/<token>/reject",
    interviewUrl: "https://app.example.com/ai-interview/<token>",
  };
  let out = template || "";
  for (const [k, v] of Object.entries(sample)) {
    out = out.split(`{{${k}}}`).join(v);
  }
  return out;
}
